import type { EntityId, SourceRecordId } from './ids.js';
import type { PrivacyClass } from './privacy.js';
import type { RecordEnvelope } from './records.js';

export type TaskLane = 'now' | 'next' | 'waiting' | 'scheduled' | 'done';

export const TASK_LANES: readonly TaskLane[] = [
  'now',
  'next',
  'waiting',
  'scheduled',
  'done',
] as const;

export type TaskReviewState = 'open' | 'needs_review' | 'reviewed';

/** Links to other canonical objects by id; never copies their data. */
export interface TaskLinks {
  readonly fromSourceRecordId: SourceRecordId | null;
  readonly relatedIds: readonly EntityId[];
}

export interface Task extends RecordEnvelope {
  readonly kind: 'task';
  readonly title: string;
  readonly lane: TaskLane;
  /** Required iff lane === 'scheduled', otherwise null (lane integrity invariant). */
  readonly scheduledFor: string | null;
  readonly links: TaskLinks;
  readonly reviewState: TaskReviewState;
}

export interface TaskInput {
  readonly title: string;
  readonly lane?: TaskLane;
  readonly scheduledFor?: string | null;
  readonly privacyClass?: PrivacyClass;
  readonly links?: Partial<TaskLinks>;
}

/** Lane integrity: 'scheduled' has a time, everything else does not. */
export function isLaneConsistent(task: Pick<Task, 'lane' | 'scheduledFor'>): boolean {
  return task.lane === 'scheduled' ? task.scheduledFor !== null : task.scheduledFor === null;
}
